import React, { useState } from 'react'
import {Calculator, Globe} from 'lucide-react'
import { US_STATES } from '../data/states'
import { COUNTRIES, getCurrencySymbol } from '../data/countries'
import { calculateInternationalPaycheck } from '../utils/internationalTaxCalculations'

interface CalculatorInputs {
  country: string
  grossPay: string
  payFrequency: 'weekly' | 'bi-weekly' | 'semi-monthly' | 'monthly' | 'annually'
  filingStatus: 'single' | 'married' | 'head-of-household'
  state: string
  includeOvertime: boolean
  includeBonus: boolean
  overtimeHours?: string
  overtimeRate?: string
  bonusAmount?: string
}

interface PayrollCalculatorProps {
  defaultState?: string
}

const PayrollCalculator: React.FC<PayrollCalculatorProps> = ({ defaultState = 'GA' }) => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    country: 'US',
    grossPay: '',
    payFrequency: 'bi-weekly',
    filingStatus: 'single',
    state: defaultState,
    includeOvertime: false,
    includeBonus: false,
    overtimeHours: '',
    overtimeRate: '1.5',
    bonusAmount: ''
  })

  const [results, setResults] = useState<any>(null)

  const currencySymbol = getCurrencySymbol(inputs.country)

  const handleCalculate = () => {
    const grossPayNum = parseFloat(inputs.grossPay) || 0
    if (grossPayNum <= 0) {
      alert('Please enter a valid gross pay amount')
      return
    }

    const calculatedResults = calculateInternationalPaycheck(
      grossPayNum,
      inputs.payFrequency,
      inputs.filingStatus,
      inputs.country,
      inputs.country === 'US' ? inputs.state : undefined
    )

    setResults(calculatedResults)
  }

  const handleInputChange = (field: keyof CalculatorInputs, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div id="calculator" className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="w-8 h-8 text-blue-800" />
        <h2 className="text-2xl font-bold text-gray-900">Paycheck Calculator</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Country */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Globe className="inline w-4 h-4 mr-1" />
            Country <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name} ({country.currencySymbol})
              </option>
            ))}
          </select>
        </div>

        {/* Gross Pay */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Gross Pay <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currencySymbol}</span>
            <input
              type="number"
              value={inputs.grossPay}
              onChange={(e) => handleInputChange('grossPay', e.target.value)}
              placeholder="5000"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Pay Frequency */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pay Frequency <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.payFrequency}
            onChange={(e) => handleInputChange('payFrequency', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-weekly</option>
            <option value="semi-monthly">Semi-monthly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </select>
        </div>

        {/* Filing Status */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Filing Status <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.filingStatus}
            onChange={(e) => handleInputChange('filingStatus', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="head-of-household">Head of Household</option>
          </select>
        </div>

        {/* State - Only show for US */}
        {inputs.country === 'US' && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              State <span className="text-red-500">*</span>
            </label>
            <select
              value={inputs.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {US_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Extra Options */}
      <div className="border-t border-gray-200 pt-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Extra Options</h3>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.includeOvertime}
              onChange={(e) => handleInputChange('includeOvertime', e.target.checked)}
              className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700">Include Overtime</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.includeBonus}
              onChange={(e) => handleInputChange('includeBonus', e.target.checked)}
              className="w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-gray-700">Include Bonus</span>
          </label>
        </div>

        {/* Conditional Overtime Fields */}
        {inputs.includeOvertime && (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overtime Hours
              </label>
              <input
                type="number"
                value={inputs.overtimeHours}
                onChange={(e) => handleInputChange('overtimeHours', e.target.value)}
                placeholder="10"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overtime Rate (multiplier)
              </label>
              <input
                type="number"
                step="0.1"
                value={inputs.overtimeRate}
                onChange={(e) => handleInputChange('overtimeRate', e.target.value)}
                placeholder="1.5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Conditional Bonus Field */}
        {inputs.includeBonus && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bonus Amount
            </label>
            <div className="relative max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currencySymbol}</span>
              <input
                type="number"
                value={inputs.bonusAmount}
                onChange={(e) => handleInputChange('bonusAmount', e.target.value)}
                placeholder="1000"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Calculate Button */}
      <button
        onClick={handleCalculate}
        className="w-full bg-blue-800 text-white font-semibold py-4 rounded-lg hover:bg-blue-900 transition-colors flex items-center justify-center gap-2"
      >
        <Calculator className="w-5 h-5" />
        Calculate Take Home Pay
      </button>

      {/* Results */}
      {results && (
        <div className="mt-8 border-t border-gray-200 pt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Paycheck Breakdown</h3>
          
          {/* Net Pay Highlight */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 mb-6">
            <div className="text-sm text-emerald-800 font-medium mb-1">Take Home Pay</div>
            <div className="text-4xl font-bold text-emerald-900">
              {currencySymbol}{results.netPay.toFixed(2)}
            </div>
            <div className="text-sm text-emerald-700 mt-1">per {inputs.payFrequency.replace('-', ' ')}</div>
          </div>

          {/* Deductions Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Gross Pay</div>
              <div className="text-2xl font-bold text-gray-900">{currencySymbol}{results.grossPay.toFixed(2)}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600 mb-1">Total Deductions</div>
              <div className="text-2xl font-bold text-red-900">{currencySymbol}{results.totalDeductions.toFixed(2)}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 mb-1">{inputs.country === 'US' ? 'Federal Tax' : 'Income Tax'}</div>
              <div className="text-xl font-bold text-blue-900">{currencySymbol}{results.incomeTax.toFixed(2)}</div>
            </div>
            {inputs.country === 'US' && results.regionalTax > 0 && (
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-purple-600 mb-1">State Tax</div>
                <div className="text-xl font-bold text-purple-900">{currencySymbol}{results.regionalTax.toFixed(2)}</div>
              </div>
            )}
            <div className="bg-cyan-50 rounded-lg p-4">
              <div className="text-sm text-cyan-600 mb-1">Social Security</div>
              <div className="text-xl font-bold text-cyan-900">{currencySymbol}{results.socialTax.toFixed(2)}</div>
            </div>
            <div className="bg-teal-50 rounded-lg p-4">
              <div className="text-sm text-teal-600 mb-1">{inputs.country === 'US' ? 'Medicare' : 'Health Tax'}</div>
              <div className="text-xl font-bold text-teal-900">{currencySymbol}{results.healthTax.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PayrollCalculator
